import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'en',
  path: '/expense-sharing-couples',
  alternatePath: '/ausgaben-teilen-paare',
  seo: {
    title: 'Expense sharing for couples — split shared costs fairly | Equna',
    description:
      'Equna is the app for couples who want to split shared expenses fairly — with a global split, receipts and a clean monthly close. Made in Germany, GDPR-compliant.',
  },
  hero: {
    eyebrow: 'For couples',
    h1: 'Expense sharing for couples — finally calm around the money topic',
    sub: 'Rent, groceries, utilities, streaming: Equna gathers your shared expenses in one place, splits them by your rule and shows at month-end who owes whom how much.',
    note: 'Start free. No tracking. Made in Germany.',
  },
  problems: {
    heading: 'Why shared expenses get messy',
    intro:
      'As long as the amounts are small, gut feeling works. With every month, costs pile up that disappear into chat threads, your head or a spreadsheet.',
    items: [
      {
        title: 'Who paid for what?',
        description:
          'One covers it now, the other next time. After three weeks no one really knows the balance.',
      },
      {
        title: 'Arguments over small things',
        description:
          "Fights are rarely about €200. They're about €8.49 — because the sense of fairness gets lost.",
      },
      {
        title: 'Receipts go missing',
        description:
          'Paper receipts fade, online orders sit in five different mailboxes, refunds get forgotten.',
      },
      {
        title: 'No clean monthly close',
        description:
          'Without a clear cutoff, the imbalance lingers for months and quietly weighs on the relationship.',
      },
    ],
  },
  solutions: {
    heading: 'What Equna actually does for couples',
    intro:
      'Set one global split rule, then logging an expense takes seconds. Both partners always see the current state.',
    items: [
      {
        icon: 'split',
        title: 'Set the split once',
        description:
          '50/50, 60/40 or proportional to income — your rule automatically applies to every shared expense.',
      },
      {
        icon: 'receipt',
        title: 'Receipts attached to the expense',
        description:
          'Snap the receipt or screenshot the online invoice — stored right on the expense, not in a drawer.',
      },
      {
        icon: 'calendar',
        title: 'Monthly close in one click',
        description:
          'Both partners confirm the month, Equna calculates the balance. What is open is clear — what is settled is done.',
      },
      {
        icon: 'home',
        title: 'Budgets for your areas',
        description:
          'Groceries, rent, leisure — define budgets and spot early when an area starts running hot.',
      },
      {
        icon: 'heart',
        title: 'Two equal views',
        description:
          'No "main user" and no "add-on". Both partners use the same app, with the same rights and the same view.',
      },
      {
        icon: 'shield',
        title: 'GDPR-compliant, no tracking',
        description:
          'Expenses, receipts and budgets are stored on servers in Germany. Currently no ads, no third-party sales.',
      },
    ],
  },
  comparison: {
    heading: 'Before vs. with Equna',
    columns: ['Without an app', 'With Equna'],
    rows: [
      {
        topic: 'Logging expenses',
        without: 'Chat messages, spreadsheets, guesswork',
        with: '10-second entry, both see it instantly',
      },
      {
        topic: 'Split rule',
        without: 'Re-negotiated every single time',
        with: 'Set once — applies to everything',
      },
      {
        topic: 'Receipts',
        without: 'Slips of paper that disappear',
        with: 'Photo attached to the expense',
      },
      {
        topic: 'Monthly close',
        without: 'Pushed off or forgotten',
        with: 'Both confirm, app calculates the balance',
      },
      {
        topic: 'Transparency',
        without: 'One has the overview, the other does not',
        with: 'Both see the same numbers',
      },
    ],
  },
  trust: {
    heading: 'Why couples trust Equna',
    items: [
      {
        title: 'Made in Germany',
        description: 'Built and operated in Berlin. Servers hosted with IONOS in Germany.',
      },
      {
        title: 'GDPR-compliant',
        description: 'Your expenses, receipts and budgets stay with you. No sharing with ad networks.',
      },
      {
        title: 'Free to start',
        description: 'The free tier covers everything couples need day-to-day — without a time limit.',
      },
      {
        title: 'No tracking',
        description: 'No ad cookies, no hidden trackers. Just an app, not a data platform.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Read more',
    intro: 'If you want to dig deeper before installing.',
    posts: [
      {
        title: 'Split household costs — five models',
        description:
          '50/50, income-proportional, the pot model and more: Which model fits your situation?',
        href: '/blog/split-household-costs-five-models',
      },
      {
        title: '3-account model and Equna — why they belong together',
        description:
          'The three-account model handles structure, Equna handles the day-to-day. Here is how they fit.',
        href: '/blog/3-account-model-and-equna-why-they-belong-together',
      },
      {
        title: 'Equna vs. Splitwise',
        description:
          "Where Splitwise shines, where Equna takes a different angle — an honest comparison for couples and families.",
        href: '/blog/equna-vs-splitwise-for-couples-and-families',
      },
    ],
  },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'Do we both need an account?',
        answer:
          'Yes. Both partners install the app, one creates the group and invites the other via invitation link. From then on, both see the same expenses in real time.',
      },
      {
        question: 'Can we change the split later?',
        answer:
          'You can adjust the global split anytime. You can also override the global rule on individual expenses when, for example, a purchase only benefits one of you.',
      },
      {
        question: 'What happens to the settlement at month-end?',
        answer:
          'Equna computes the balance. You make the actual transfer yourselves — the app does not lock you into any payment provider.',
      },
      {
        question: 'What if we do not have a receipt?',
        answer:
          'You can log expenses without a receipt. For online purchases, a screenshot of the order confirmation or the bank entry is enough.',
      },
      {
        question: 'Is Equna free?',
        answer:
          'Yes. The free tier includes everything couples need: expenses, split, receipts (60 days), monthly close, three budget areas. An optional premium tier with longer receipt storage and annual budgets is planned.',
      },
    ],
  },
  finalCta: {
    heading: 'Ready to take the noise out of money?',
    sub: 'Install Equna, invite your partner, set the split — done.',
  },
  uiStrings: {
    backToHome: '← Back to home',
    related: 'Read more',
    legalPrivacy: 'Privacy',
    legalTerms: 'Terms',
    legalImprint: 'Imprint',
    legalSupport: 'Support',
    copyrightRights: 'All rights reserved.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Expense sharing for couples',
  },
}

export default function CouplesSeoPage() {
  return <SeoLandingLayout content={content} />
}
